import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from './cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'accent';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  fullWidth?: boolean;
}

// Gradient primary/accent carry a soft brand glow and brighten on hover
// (filter transitions smoothly where a gradient swap can't). Secondary uses
// a ring + faint elevation; everything presses 2% on :active for a tactile feel.
const VARIANTS: Record<Variant, string> = {
  primary:
    'text-brand-foreground [background-image:var(--gradient-brand)] shadow-glow-brand hover:brightness-110 disabled:bg-none disabled:bg-foreground-subtle disabled:shadow-none disabled:brightness-100',
  secondary:
    'bg-surface text-foreground ring-1 ring-inset ring-border-strong/70 shadow-e1 hover:bg-surface-muted hover:ring-border-strong disabled:text-foreground-subtle disabled:bg-surface disabled:shadow-none',
  ghost:
    'bg-transparent text-foreground-muted hover:bg-surface-muted hover:text-foreground disabled:text-foreground-subtle',
  destructive:
    'bg-danger text-brand-foreground shadow-e1 hover:bg-danger-hover disabled:bg-foreground-subtle disabled:shadow-none',
  accent:
    'text-brand-foreground [background-image:var(--gradient-brand)] shadow-glow-brand hover:brightness-110 disabled:bg-none disabled:bg-foreground-subtle disabled:shadow-none disabled:brightness-100',
};

const SIZES: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5 rounded-md',
  md: 'h-10 px-4 text-sm gap-2 rounded-md',
  lg: 'h-12 px-6 text-[0.95rem] gap-2 rounded-lg',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading,
    startIcon,
    endIcon,
    fullWidth,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        'inline-flex select-none items-center justify-center font-medium',
        'transition-[transform,box-shadow,background-color,filter,color] duration-150 ease-out',
        'active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70 disabled:active:scale-100',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <span
          aria-hidden
          className="inline-block size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      ) : (
        startIcon
      )}
      {children}
      {!loading && endIcon}
    </button>
  );
});
